import { copy } from '../copy.js';
import { dateShort } from '../dates.js';
import type { ComposeState } from '../state/derive.js';
import { isYou, meOf, postedToday, todayOf } from '../state/lookup.js';
import type { AppState } from '../state/types.js';
import { color } from '../tokens.js';
import { pfpOf } from './picture.js';
import type { Pfp } from './picture.js';

/** The island, ported from `islandVals` (02 §2.5, §3.11–3.12; 03 §6). */

export type IslandMode = 'cal' | 'day' | 'person' | 'other' | 'own' | 'compose' | 'cask' | 'edit' | 'pask';

/**
 * Width per mode. `own` and `other` are wider than in the prototype (330 and 400): by the owner's
 * decision of 2 October 2026 the Home button now sits in the island on profiles.
 */
export const ISLAND_WIDTH: Readonly<Record<IslandMode, number>> = { cal: 320, day: 400, person: 520, other: 520, own: 400, compose: 560, cask: 480, edit: 560, pask: 440 };

export interface IslandVals {
  readonly mode: IslandMode;
  readonly width: number;
  readonly left: number;
  /** Bar modes (composer, profile edit) lay out Cancel · switch · + · actions. */
  readonly bar: boolean;
  /** Your picture and name (opens your profile). */
  readonly me: { readonly show: boolean; readonly name: string; readonly aria: string; readonly off: boolean; readonly picture: Pfp };
  readonly showEdit: boolean;
  readonly editColor: string;
  readonly confirm: { readonly text: string; readonly label: string } | null;
  /**
   * Post today, or once you posted (owner, 4 October 2026): View your post everywhere except on that
   * post, where it is Edit. `action` says which: open the composer or open your post.
   * `off`: Edit, and the backend cannot edit posts (06 §4, `editPost` false).
   */
  readonly post: { readonly label: string; readonly action: 'compose' | 'view'; readonly isNew: boolean; readonly off: boolean } | null;
  readonly addOpen: boolean;
  readonly linesOn: boolean;
  readonly switchTrack: string;
  readonly look: { readonly on: boolean; readonly color: string } | null;
  readonly showDelete: boolean;
  readonly done: { readonly label: string; readonly off: boolean };
}

export function islandMode(st: AppState): IslandMode {
  const v = st.view;
  if (v === 'compose') return st.cAsk ? 'cask' : 'compose';
  if (v === 'person') return st.pp === 'in-pre' || st.pp === 'out' ? 'day' : 'person';
  if (v === 'day') return st.cd === 'in-pre' || st.cd === 'out' ? 'cal' : 'day';
  if (v === 'profile') return st.pe ? (st.peAsk ? 'pask' : 'edit') : isYou(st, st.profileOf) ? 'own' : 'other';
  return 'cal';
}

/** You are looking at your own post for today: the one post the island's Edit opens. */
export const onOwnPost = (st: AppState): boolean => st.view === 'person' && isYou(st, st.person) && st.date === todayOf(st);

export function islandVals(st: AppState, cs: ComposeState): IslandVals {
  const me = meOf(st), mode = islandMode(st), width = ISLAND_WIDTH[mode], bar = mode === 'compose' || mode === 'edit';
  const pe = st.pe, lines = st.view === 'compose' && st.draft ? st.draft.lines : pe ? pe.lines : null, linesOn = lines !== 'plain';
  const posted = postedToday(st), del = st.cAsk === 'delete';
  const flatPost = mode === 'cal' || mode === 'day' || mode === 'person' || mode === 'other' || mode === 'own';
  return {
    mode, width, left: 720 - width / 2, bar,
    me: {
      show: mode === 'cal' || mode === 'day' || mode === 'person' || mode === 'other',
      name: me.name, aria: copy.island.yourProfile(me.name), off: st.lv, picture: pfpOf(st, pe ? pe.color : me.color, pe ? pe.pic : me.pic),
    },
    showEdit: mode === 'own',
    editColor: me.color,
    confirm: mode === 'cask' || mode === 'pask'
      ? { text: mode === 'pask' ? copy.island.discardChanges : del ? copy.island.deletePost(dateShort(st.kDate)) : copy.island.discardDraft, label: del ? copy.island.delete : copy.island.discard }
      : null,
    post: !flatPost ? null : !posted ? { label: copy.island.postToday, action: 'compose', isNew: true, off: false }
      : onOwnPost(st) ? { label: copy.island.edit, action: 'compose', isNew: false, off: !st.caps.editPost }
        : { label: copy.island.viewPost, action: 'view', isNew: false, off: false },
    addOpen: st.tray === 'add',
    linesOn,
    switchTrack: linesOn ? (pe ? pe.color : me.color) : color.line,
    look: mode === 'edit' ? { on: st.tray === 'look', color: pe ? pe.color : me.color } : null,
    showDelete: mode === 'compose' && st.cEditing && st.caps.deletePost,
    done: mode === 'edit'
      ? { label: st.peStatus === 'saving' ? copy.island.saving : st.peStatus === 'failed' ? copy.tryAgain : copy.island.done, off: false }
      : { label: cs.saving ? copy.island.saving : cs.failed ? copy.tryAgain : st.cEditing ? copy.island.save : copy.island.share, off: mode === 'compose' ? !cs.can : false },
  };
}
